import {
  Box,
  HStack,
  Link,
  List,
  ListItem,
  Spacer,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { fromPairs, isEmpty } from 'lodash';
import React, { useState } from 'react';
import { PieChart } from 'react-minimal-pie-chart';
import { useTranslation } from 'react-i18next';
import { OrderStatus, ProjectDeepFragment } from '../../../gql/graphql';
import {
  ResolvedDbIdsReturn,
  orderStatusColorMapper,
  useOrderStatusColor,
} from '../../../hooks/order';
import { mergeRecordOfLists } from '../../../services/viewer-services';
import { useColorization } from '../../../services/viewer/colorization';
import CircleIcon from '../../common/CircleIcon';
import { DbIdsPerModel, useViewer } from '../../common/ForgeViewer';

interface OrderStatusOverviewProps {
  project: ProjectDeepFragment | null;
  resolvedDbIds: ResolvedDbIdsReturn;
}

export default function OrderStatusOverview({
  project,
  resolvedDbIds,
}: OrderStatusOverviewProps) {
  const [selected, setSelected] = useState<number | undefined>(undefined);
  const { loadedModels, isAllModelsLoaded } = useViewer();
  const { colorSection, clearColors } = useColorization();
  const labelColor = useColorModeValue('gray.500', 'gray.300');

  const { t } = useTranslation('project');

  const { orderStatusHexMapper } = useOrderStatusColor();

  const colorizeBasedOnOrderStatus = (orderStatus: OrderStatus) => {
    const color = orderStatusHexMapper(orderStatus);
    if (
      !isEmpty(loadedModels) &&
      isAllModelsLoaded &&
      !resolvedDbIds.isLoading
    ) {
      let dbIdsToColor: DbIdsPerModel = {};
      project?.orders?.forEach((order) => {
        if (order.status === orderStatus) {
          const dbIds = resolvedDbIds.getDbIdsForOrder(order.id);
          dbIdsToColor = mergeRecordOfLists(
            dbIdsToColor,
            fromPairs(dbIds.map(({ modelUrn, dbIds }) => [modelUrn, dbIds]))
          );
        }
      });
      colorSection(() => color, dbIdsToColor);
    }
  };

  const numberOfOpenOrders = project?.orders.filter(
    (order) => order.status === OrderStatus.Open
  ).length;
  const numberOfOrdersInProgress = project?.orders.filter(
    (order) => order.status === OrderStatus.InProgress
  ).length;
  const numberOfCompletedOrders = project?.orders.filter(
    (order) => order.status === OrderStatus.Completed
  ).length;
  const data = [
    {
      value: numberOfOpenOrders ? numberOfOpenOrders : 0,
      color: orderStatusHexMapper(OrderStatus.Open),
    },
    {
      value: numberOfOrdersInProgress ? numberOfOrdersInProgress : 0,
      color: orderStatusHexMapper(OrderStatus.InProgress),
    },
    {
      value: numberOfCompletedOrders ? numberOfCompletedOrders : 0,
      color: orderStatusHexMapper(OrderStatus.Completed),
    },
  ];

  const colorizeBasedOnSelectedSegmentIndex = async (
    e: React.SyntheticEvent,
    segmentindex: number
  ) => {
    if (segmentindex === selected && !isEmpty(loadedModels)) {
      clearColors();
      setSelected(undefined);
      return;
    }

    switch (segmentindex) {
      case 0:
        colorizeBasedOnOrderStatus(OrderStatus.Open);
        break;
      case 1:
        colorizeBasedOnOrderStatus(OrderStatus.InProgress);
        break;
      case 2:
        colorizeBasedOnOrderStatus(OrderStatus.Completed);
        break;
      default:
        break;
    }

    setSelected(segmentindex);
  };

  return project && project.orders.length > 0 ? (
    <HStack margin={'auto'} width={'100%'}>
      <Spacer />
      <Box width={'90px'}>
        <PieChart
          onClick={colorizeBasedOnSelectedSegmentIndex}
          lineWidth={40}
          radius={45}
          data={data}
          segmentsShift={(index) => (index === selected ? 3 : 0)}
          segmentsStyle={{
            transition: 'stroke .3s',
            cursor: 'pointer',
          }}
        />
      </Box>
      <List>
        <ListItem>
          <Link onClick={(e) => colorizeBasedOnSelectedSegmentIndex(e, 0)}>
            <HStack>
              <CircleIcon
                boxSize={3}
                color={orderStatusColorMapper(OrderStatus.Open)}
                mb={1}
              />
              <Text fontSize="xs" color={labelColor}>
                {t('table-status.open')} ({numberOfOpenOrders})
              </Text>
            </HStack>
          </Link>
        </ListItem>
        <ListItem>
          <Link onClick={(e) => colorizeBasedOnSelectedSegmentIndex(e, 1)}>
            <HStack>
              <CircleIcon
                boxSize={3}
                color={orderStatusColorMapper(OrderStatus.InProgress)}
                mb={1}
              />
              <Text fontSize={'xs'} color={labelColor}>
                {t('table-status.in-progress')} ({numberOfOrdersInProgress})
              </Text>
            </HStack>
          </Link>
        </ListItem>
        <ListItem>
          <Link onClick={(e) => colorizeBasedOnSelectedSegmentIndex(e, 2)}>
            <HStack>
              <CircleIcon
                boxSize={3}
                color={orderStatusColorMapper(OrderStatus.Completed)}
                mb={1}
              />
              <Text fontSize="xs" color={labelColor}>
                {t('table-status.completed')} ({numberOfCompletedOrders})
              </Text>
            </HStack>
          </Link>
        </ListItem>
      </List>
      <Spacer />
    </HStack>
  ) : (
    <Text color={labelColor}>{t('project-status-panel.placeholder')}</Text>
  );
}
